import { VariableType } from 'json-to-graphql-query';

import { View } from '@/interfaces/view';
import { EntityResource } from './entity.resource';

/**
 * Resource widoku
 *
 * @export
 * @class ViewResource
 * @extends {EntityResource<View>}
 */
export class ViewResource extends EntityResource<View> {
  public listVariables = { filters: 'ViewFilters' };

  public getFields = {
    id: true,
    name: true,
    mask: true,
    body: true,
    headerId: true,
    hideHeaderOnFirstPage: true,
    footerId: true,
    hideFooterOnFirstPage: true,
    schemaId: true,
    singleView: true,
    schema: {
      id: true,
      name: true,
      schema: true
    }
  };

  public listFields = {
    id: true,
    name: true,
    printOptions: true,
    singleView: true,
    schema: {
      id: true,
      name: true,
      schema: true
    },
    data: true
  };

  constructor() {
    super({
      entityName: 'view'
    });
  }

  /**
   * Pobranie listy po identyfikatorach
   *
   * @param {number[]} ids
   * @returns {Promise<View[]>}
   * @memberof ViewResource
   */
  public listByIds(ids: number[]): Promise<View[]> {
    const query = this.prepareOptions({
      query: {
        __variables: {
          ids: '[Int!]!'
        },
        viewListByIds: {
          __args: { ids: new VariableType('ids') },
          id: true,
          name: true,
          schema: {
            id: true,
            name: true,
            schema: true
          }
        }
      }
    });
    return this.query({ query, variables: { ids } }, 'viewListByIds');
  }

  /**
   * Zapisuje widok jako załącznik
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  public saveAsAttachment(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          viewId: 'Int!',
          documentId: 'Int!',
          data: 'JSON'
        },
        viewSaveToAttachment: {
          __args: {
            viewId: new VariableType('viewId'),
            documentId: new VariableType('documentId'),
            data: new VariableType('data')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'viewSaveToAttachment');
  }

  /**
   * Zapisuje widok wygenerowany grupowo
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  public saveGroupAsAttachment(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          viewId: 'Int!',
          procedureId: 'Int!',
          data: 'JSON',
          singleView: 'Boolean'
        },
        viewGroupSaveToAttachment: {
          __args: {
            viewId: new VariableType('viewId'),
            procedureId: new VariableType('procedureId'),
            data: new VariableType('data'),
            singleView: new VariableType('singleView')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'viewGroupSaveToAttachment');
  }

  /**
   * Zapisuje widok jako załącznik
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  public sendToMDOK(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          viewId: 'Int!',
          documentId: 'Int!',
          data: 'JSON'
        },
        viewSendToMDOK: {
          __args: {
            viewId: new VariableType('viewId'),
            documentId: new VariableType('documentId'),
            data: new VariableType('data')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'viewSendToMDOK');
  }

  /**
   * Zapisuje widok wygenerowany grupowo i przesyła go do MDOK
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  public sendGroupToMDOK(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          viewId: 'Int!',
          procedureId: 'Int!',
          data: 'JSON',
          singleView: 'Boolean',
          caseId: 'Int'
        },
        viewGroupSendToMDOK: {
          __args: {
            viewId: new VariableType('viewId'),
            procedureId: new VariableType('procedureId'),
            data: new VariableType('data'),
            singleView: new VariableType('singleView'),
            caseId: new VariableType('caseId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'viewGroupSendToMDOK');
  }

  /**
   * Zapisuje wyedytowany widok jako załącznik
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  public saveEditedHtmlAsAttachment(variables: any): Promise<number> {
    return this.saveEditedHtml(variables, 'htmlSaveToAttachment');
  }

  /**
   * Zapisuje wyedytowany widok do MDOK
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  public saveEditedHtmlToMDOK(variables: any): Promise<number> {
    return this.saveEditedHtml(variables, 'htmlSendToMDOK');
  }

  /**
   * Zapisuje wyedytowany widok
   *
   * @param {*} variables
   * @param {string} mutationName
   * @returns {Promise<number>}
   * @memberof ViewResource
   */
  private saveEditedHtml(variables: any, mutationName: string): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          html: 'String!',
          data: 'JSON!',
          viewId: 'Int!',
          documentId: 'Int!'
        },
        [mutationName]: {
          __args: {
            html: new VariableType('html'),
            data: new VariableType('data'),
            viewId: new VariableType('viewId'),
            documentId: new VariableType('documentId')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, mutationName);
  }
}

export const viewResource = new ViewResource();
